// ── The shared Dummy / Books Adjustment code — one running balance ──────────
// One InventoryItem per business (BusinessSettings.dummyInventoryItemId,
// category 'books-dummy'), created lazily the first time anything credits
// it. It is a running balance of carats + cost, not a physical stone, and
// may go below zero (see "Dummy / Books Adjustment code" in CLAUDE.md).
//
// Two things ADD to it, both through this module:
//   · a Sale's 'cash-real' lines (stock physically left, Books never knew) —
//     Sales.tsx's planDummyCreditDelta, history id `dummycredit_{saleId}`
//   · a Purchase's 'books-dummy' lines (a real invoice we pay, but no
//     stone arrives — the carats only top the Dummy balance back up) —
//     Purchase.tsx, history id `dummypurchase_{invoiceId}`
// Selling FROM it is an ordinary (special-cased) sale line in Sales.tsx.
//
// Each source is invoice-scoped: its whole credit is stored on one history
// doc under the Dummy Code, and every save applies only the DELTA from what
// that doc says was credited last time — so editing or deleting the source
// self-corrects the balance instead of double-counting. Adding carats at
// cost adds to both carat and value, so the cost/ct (value ÷ carats)
// re-averages naturally.
//
// Usage inside one runTransaction (all reads before any write):
//   const plan = await planDummyDelta(tx, db, {...});   // read phase
//   applyDummyDelta(tx, db, plan, {...});               // write phase
// If plan.isNewDummyItem, the caller must persist `cursor.n` back to
// counters/{businessId}.itemCode in its own (single) counter write.

import {
  collection, doc, serverTimestamp,
  type DocumentReference, type Firestore, type Transaction,
} from 'firebase/firestore';
import type { BusinessSettings, InventoryItem, InventoryStatus, PurchaseType, TransactionCategory } from '../types';

export interface DummyAggregate { carat: number; costUSD: number; costingUSD: number }

export interface DummyDeltaPlan {
  businessId:     string;
  settingsRef:    DocumentReference;
  dummyRef:       DocumentReference;
  isNewDummyItem: boolean;
  dummyCode?:     string;            // only when isNewDummyItem
  dummyCodeId:    string;
  newCarat: number; newCostUSD: number; newCostingUSD: number;
  historyRef:     DocumentReference;
  hasCredit:      boolean;           // false => delete the history doc
  aggregate:      DummyAggregate;
  needsItemWrite: boolean;           // false => this save doesn't move the balance at all
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}

/**
 * Write phase only: the Dummy item itself, its Code registry entry, and the
 * settings pointer to it. Shared by the lazy auto-create below and by
 * Settings' "Create Dummy Code" (where the owner picks the Code up front).
 */
export function writeNewDummyItem(tx: Transaction, db: Firestore, o: {
  ref: DocumentReference; businessId: string; code: string; createdBy: string;
  carat?: number; costUSD?: number; costingUSD?: number;
}): void {
  const codeId = normalizeCode(o.code);
  tx.set(o.ref, stripUndef({
    businessId: o.businessId,
    purchaseId: 'dummy', purchaseNo: 'DUMMY', lineItemId: '',
    type: 'polished-loose' as PurchaseType,
    status: 'in-stock' as InventoryStatus,
    shape: 'Mixed', carat: o.carat ?? 0, color: '-', clarity: '-',
    code: o.code, codeId,
    category: 'books-dummy' as TransactionCategory,
    costUSD: o.costUSD ?? 0, costingUSD: o.costingUSD ?? 0,
    createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  }));
  tx.set(doc(db, 'itemCodes', codeId), stripUndef({
    businessId: o.businessId, code: o.code, inventoryItemId: o.ref.id,
    createdAt: serverTimestamp(), createdBy: o.createdBy,
  }));
  tx.set(doc(db, 'settings', o.businessId), { dummyInventoryItemId: o.ref.id }, { merge: true });
}

/** A Dummy code the owner types is exactly 5 digits, like the app's normal Codes (20001, 20002…). */
export const DUMMY_CODE_RE = /^\d{5}$/;
const DUMMY_CODE_MSG = 'The Dummy code must be exactly 5 digits.';

/** Settings › Custom Options: create the Dummy code with a Code the owner chose. */
export async function createDummyCode(tx: Transaction, db: Firestore, o: {
  businessId: string; code: string; createdBy: string;
}): Promise<void> {
  const code = o.code.trim();
  if (!DUMMY_CODE_RE.test(code)) throw new Error(DUMMY_CODE_MSG);
  const settingsSnap = await tx.get(doc(db, 'settings', o.businessId));
  const existingId = (settingsSnap.data() as BusinessSettings | undefined)?.dummyInventoryItemId;
  if (existingId && (await tx.get(doc(db, 'inventory', existingId))).exists()) {
    throw new Error('A Dummy code already exists.');
  }
  if ((await tx.get(doc(db, 'itemCodes', normalizeCode(code)))).exists()) {
    throw new Error(`Code "${code}" is already used by another item — choose a different one.`);
  }
  writeNewDummyItem(tx, db, { ref: doc(collection(db, 'inventory')), businessId: o.businessId, code, createdBy: o.createdBy });
}

/**
 * Change the Dummy code's Code — only while it's unused (zero balance and no
 * history), so its lifecycle history never ends up split across two Codes.
 * `hasHistory` is checked by the caller beforehand (a collection query can't
 * run inside a transaction).
 */
export async function renameDummyCode(tx: Transaction, db: Firestore, o: {
  itemId: string; newCode: string; createdBy: string; hasHistory: boolean;
}): Promise<void> {
  const code = o.newCode.trim();
  if (!DUMMY_CODE_RE.test(code)) throw new Error(DUMMY_CODE_MSG);
  if (o.hasHistory) throw new Error('This Dummy code has already been used, so its code can no longer be changed.');
  const itemRef = doc(db, 'inventory', o.itemId);
  const itemSnap = await tx.get(itemRef);
  if (!itemSnap.exists()) throw new Error('The Dummy code no longer exists.');
  const item = itemSnap.data() as InventoryItem;
  if (Math.abs(item.carat ?? 0) > 1e-9 || Math.abs(item.costUSD ?? 0) > 1e-6) {
    throw new Error('This Dummy code already holds a balance, so its code can no longer be changed.');
  }
  const newId = normalizeCode(code);
  if (newId === item.codeId) return;
  if ((await tx.get(doc(db, 'itemCodes', newId))).exists()) {
    throw new Error(`Code "${code}" is already used by another item — choose a different one.`);
  }
  tx.update(itemRef, { code, codeId: newId, updatedAt: serverTimestamp() });
  tx.set(doc(db, 'itemCodes', newId), { businessId: item.businessId, code, inventoryItemId: o.itemId, createdAt: serverTimestamp(), createdBy: o.createdBy });
  if (item.codeId) tx.delete(doc(db, 'itemCodes', item.codeId));
}

export async function planDummyDelta(tx: Transaction, db: Firestore, o: {
  businessId: string;
  historyId:  string;          // e.g. `dummypurchase_${invoiceId}`
  aggregate:  DummyAggregate;  // this source's FULL credit as of this save (0s on delete)
  cursor:     { n: number };   // the caller's live itemCode cursor (shared, so no number is handed out twice)
  reserved:   Set<string>;
  nextCode:   (tx: Transaction, db: Firestore, cursor: { n: number }, reserved: Set<string>) => Promise<string>;
}): Promise<DummyDeltaPlan | null> {
  const settingsRef  = doc(db, 'settings', o.businessId);
  const settingsSnap = await tx.get(settingsRef);
  const existingId   = (settingsSnap.data() as BusinessSettings | undefined)?.dummyInventoryItemId;
  const existingSnap = existingId ? await tx.get(doc(db, 'inventory', existingId)) : null;
  const exists = !!existingSnap?.exists();

  // Nothing to credit and no Dummy item whose history could need reversing.
  if (o.aggregate.carat <= 1e-9 && !exists) return null;

  let dummyRef: DocumentReference;
  let isNewDummyItem = false;
  let dummyCode: string | undefined;
  let dummyCodeId: string;
  let liveCarat = 0, liveCostUSD = 0, liveCostingUSD = 0;
  if (exists) {
    const d = existingSnap!.data() as InventoryItem;
    dummyRef = existingSnap!.ref;
    dummyCodeId = d.codeId!;
    liveCarat = d.carat; liveCostUSD = d.costUSD; liveCostingUSD = d.costingUSD;
  } else {
    dummyRef = doc(collection(db, 'inventory'));
    isNewDummyItem = true;
    dummyCode = await o.nextCode(tx, db, o.cursor, o.reserved);
    dummyCodeId = normalizeCode(dummyCode);
  }

  const historyRef = doc(db, 'itemCodes', dummyCodeId, 'history', o.historyId);
  const priorSnap  = await tx.get(historyRef);
  const prior = priorSnap.exists() ? priorSnap.data() : null;
  const deltaCarat      = o.aggregate.carat      - ((prior?.carat as number | undefined) ?? 0);
  const deltaCostUSD    = o.aggregate.costUSD    - ((prior?.amountUSD as number | undefined) ?? 0);
  const deltaCostingUSD = o.aggregate.costingUSD - ((prior?.costingUSD as number | undefined) ?? 0);

  const needsItemWrite = isNewDummyItem
    || Math.abs(deltaCarat) > 1e-9 || Math.abs(deltaCostUSD) > 1e-6 || Math.abs(deltaCostingUSD) > 1e-6;

  return {
    businessId: o.businessId, settingsRef, dummyRef, isNewDummyItem, dummyCode, dummyCodeId,
    // No floor at zero — the balance may legitimately go negative.
    newCarat: round2(liveCarat + deltaCarat),
    newCostUSD: round2(liveCostUSD + deltaCostUSD),
    newCostingUSD: round2(liveCostingUSD + deltaCostingUSD),
    historyRef, hasCredit: o.aggregate.carat > 1e-9, aggregate: o.aggregate, needsItemWrite,
  };
}

export function applyDummyDelta(tx: Transaction, db: Firestore, plan: DummyDeltaPlan | null, h: {
  type:      'dummy-credit' | 'purchase';
  date:      string;
  entityId:  string;
  category:  TransactionCategory;
  notes:     string;
  createdBy: string;
  extra?:    Record<string, unknown>;   // e.g. purchaseInvoiceId / purchaseInvoiceNo
}): void {
  // Nothing moved since the last save — the history doc already says so.
  if (!plan || !plan.needsItemWrite) return;
  {
    if (plan.isNewDummyItem) {
      writeNewDummyItem(tx, db, {
        ref: plan.dummyRef, businessId: plan.businessId, code: plan.dummyCode!, createdBy: h.createdBy,
        carat: plan.newCarat, costUSD: plan.newCostUSD, costingUSD: plan.newCostingUSD,
      });
    } else {
      // Always back to 'in-stock' — the Dummy code never "sells out".
      tx.update(plan.dummyRef, {
        carat: plan.newCarat, costUSD: plan.newCostUSD, costingUSD: plan.newCostingUSD,
        status: 'in-stock' as InventoryStatus,
        soldToEntityId: null, soldDate: null, soldAmountUSD: null,
        updatedAt: serverTimestamp(),
      });
    }
  }
  if (plan.hasCredit) {
    // amountUSD = the credited COST (standard CodeHistoryEntry field);
    // costingUSD is kept alongside so the next save can recover the exact
    // previous split for its delta.
    tx.set(plan.historyRef, stripUndef({
      type: h.type, date: h.date, carat: plan.aggregate.carat, entityId: h.entityId,
      pricePerCt: plan.aggregate.carat > 0 ? plan.aggregate.costUSD / plan.aggregate.carat : 0,
      amountUSD: plan.aggregate.costUSD, costingUSD: plan.aggregate.costingUSD,
      category: h.category, inventoryItemId: plan.dummyRef.id, notes: h.notes,
      ...(h.extra ?? {}),
      createdAt: serverTimestamp(), createdBy: h.createdBy,
    }));
  } else {
    tx.delete(plan.historyRef);
  }
}
